import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import supervisorService from '../services/supervisorService';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';

export default function TeamMembersModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchTeamMembers();
    }
  }, [isOpen]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await supervisorService.getTeamMembers();
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Team Members</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner size="lg" />
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No team members</h3>
              <p className="mt-2 text-gray-600">You don't have any drivers assigned to you yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {teamMembers.map((driver) => (
                <div
                  key={driver.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm">
                      {driver.user?.first_name?.[0]?.toUpperCase() || 'D'}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {driver.user?.first_name} {driver.user?.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{driver.user?.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {driver.user?.is_active ? (
                      <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                        Inactive
                      </span>
                    )}
                    <Link
                      to={`/logs?driver_id=${driver.id}`}
                      onClick={onClose}
                      className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                    >
                      View Logs
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && teamMembers.length > 0 && (
          <div className="px-6 py-4 border-t bg-gray-50">
            <div className="text-sm text-gray-600">
              Total: {teamMembers.length} {teamMembers.length === 1 ? 'driver' : 'drivers'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
